import { AuthenticationType } from '@/lib/api/common/interfaces/integration.interface'

interface AuthenticationFieldsProps {
    authenticationType: AuthenticationType
    authenticationData: any
    onChange: (data: any) => void
    prefix?: string // Para diferenciar primeira e segunda chamada
}

export default function AuthenticationFields({
    authenticationType,
    authenticationData,
    onChange,
    prefix = '',
}: AuthenticationFieldsProps) {
    const updateField = (field: string, value: any) => {
        onChange({
            ...authenticationData,
            [field]: value,
        })
    }

    const getFieldId = (field: string) => {
        return prefix ? `${prefix}_${field}` : field
    }

    const getFieldName = (field: string) => {
        return prefix ? `${prefix}.${field}` : field
    }

    switch (authenticationType) {
        case AuthenticationType.NONE:
            return null

        case AuthenticationType.BASIC:
            return (
                <div className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Username <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('username')}
                            name={getFieldName('username')}
                            className="input input-bordered w-full"
                            placeholder="Username para autenticação básica"
                            value={authenticationData?.username || ''}
                            onChange={e => updateField('username', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Password <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="password"
                            id={getFieldId('password')}
                            name={getFieldName('password')}
                            className="input input-bordered w-full"
                            placeholder="Password para autenticação básica"
                            value={authenticationData?.password || ''}
                            onChange={e => updateField('password', e.target.value)}
                            required
                        />
                    </div>
                </div>
            )

        case AuthenticationType.BEARER:
            return (
                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Token <span className="text-error">*</span>
                        </span>
                    </label>
                    <input
                        type="text"
                        id={getFieldId('token')}
                        name={getFieldName('token')}
                        className="input input-bordered w-full"
                        placeholder="Bearer token"
                        value={authenticationData?.token || ''}
                        onChange={e => updateField('token', e.target.value)}
                        required
                    />
                </div>
            )

        case AuthenticationType.API_KEY:
            return (
                <div className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                API Key <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('apiKey')}
                            name={getFieldName('apiKey')}
                            className="input input-bordered w-full"
                            placeholder="Chave da API"
                            value={authenticationData?.apiKey || ''}
                            onChange={e => updateField('apiKey', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Nome do Header{' '}
                                <span className="text-gray-400 text-sm">
                                    (opcional)
                                </span>
                            </span>
                            <span className="label-text-alt">
                                Padrão: X-API-Key
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('headerName')}
                            name={getFieldName('headerName')}
                            className="input input-bordered w-full"
                            placeholder="X-API-Key"
                            value={authenticationData?.headerName || 'X-API-Key'}
                            onChange={e =>
                                updateField('headerName', e.target.value)
                            }
                        />
                    </div>
                </div>
            )

        case AuthenticationType.OAUTH2:
            return (
                <div className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Client ID <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('clientId')}
                            name={getFieldName('clientId')}
                            className="input input-bordered w-full"
                            placeholder="OAuth2 Client ID"
                            value={authenticationData?.clientId || ''}
                            onChange={e => updateField('clientId', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Client Secret{' '}
                                <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="password"
                            id={getFieldId('clientSecret')}
                            name={getFieldName('clientSecret')}
                            className="input input-bordered w-full"
                            placeholder="OAuth2 Client Secret"
                            value={authenticationData?.clientSecret || ''}
                            onChange={e =>
                                updateField('clientSecret', e.target.value)
                            }
                            required
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Token URL <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="url"
                            id={getFieldId('tokenUrl')}
                            name={getFieldName('tokenUrl')}
                            className="input input-bordered w-full"
                            placeholder="https://api.example.com/oauth/token"
                            value={authenticationData?.tokenUrl || ''}
                            onChange={e => updateField('tokenUrl', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Scope{' '}
                                <span className="text-gray-400 text-sm">
                                    (opcional)
                                </span>
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('scope')}
                            name={getFieldName('scope')}
                            className="input input-bordered w-full"
                            placeholder="read write"
                            value={authenticationData?.scope || ''}
                            onChange={e => updateField('scope', e.target.value)}
                        />
                    </div>
                </div>
            )

        case AuthenticationType.CLIENT_CREDENTIALS:
            return (
                <div className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Client ID <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('clientId')}
                            name={getFieldName('clientId')}
                            className="input input-bordered w-full"
                            placeholder="Client ID"
                            value={authenticationData?.clientId || ''}
                            onChange={e => updateField('clientId', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Client Secret{' '}
                                <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="password"
                            id={getFieldId('clientSecret')}
                            name={getFieldName('clientSecret')}
                            className="input input-bordered w-full"
                            placeholder="Client Secret"
                            value={authenticationData?.clientSecret || ''}
                            onChange={e =>
                                updateField('clientSecret', e.target.value)
                            }
                            required
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Token URL <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="url"
                            id={getFieldId('tokenUrl')}
                            name={getFieldName('tokenUrl')}
                            className="input input-bordered w-full"
                            placeholder="https://api.example.com/oauth/token"
                            value={authenticationData?.tokenUrl || ''}
                            onChange={e => updateField('tokenUrl', e.target.value)}
                            required
                        />
                    </div>
                </div>
            )

        case AuthenticationType.JWT:
            return (
                <div className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Secret <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('secret')}
                            name={getFieldName('secret')}
                            className="input input-bordered w-full"
                            placeholder="JWT Secret"
                            value={authenticationData?.secret || ''}
                            onChange={e => updateField('secret', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Issuer{' '}
                                <span className="text-gray-400 text-sm">
                                    (opcional)
                                </span>
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('issuer')}
                            name={getFieldName('issuer')}
                            className="input input-bordered w-full"
                            placeholder="JWT Issuer"
                            value={authenticationData?.issuer || ''}
                            onChange={e => updateField('issuer', e.target.value)}
                        />
                    </div>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Audience{' '}
                                <span className="text-gray-400 text-sm">
                                    (opcional)
                                </span>
                            </span>
                        </label>
                        <input
                            type="text"
                            id={getFieldId('audience')}
                            name={getFieldName('audience')}
                            className="input input-bordered w-full"
                            placeholder="JWT Audience"
                            value={authenticationData?.audience || ''}
                            onChange={e => updateField('audience', e.target.value)}
                        />
                    </div>
                </div>
            )

        default:
            return null
    }
}

